// ./src/components/features/Revenues/RevenuesList/RevenuesList.tsx
//
// List component for the Revenues page.
// Renders revenue records as chronological cards using RecordCard
// with Framer Motion entrance animations.
//
// Context support (matches LogbookList pattern):
// - When rendered inside an <ExpensesProvider>, data props (records, loading,
//   loadingMore, hasMore, onLoadMore, deletingId) are read from context
//   automatically. Props can still override context values if provided.
// - When rendered outside a provider, all data must be passed via props.
//
// Design decisions:
// - Cards only (no table view) — consistent across all breakpoints
// - Single-column layout (timeline-style) for chronological clarity
// - Staggered entrance animation on first load
// - SwipeableCard actions on mobile, inline actions on desktop via RecordCard

import React, { useMemo, useCallback } from "react";
import {
  Paper,
  Stack,
  Text,
  Badge,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

import { detectOdometerAnomalies } from "@/utils/expenseHelpers";
import { Expense } from "@/utils/api";

import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";

import {
  useUserProfileContext,
  useAppEssentialsContext,
  useDashboardContext,
  useOptionalExpensesContext,
} from "@/contexts";

import {
  EmptyState,
  useSwipeableList,
  RecordCard,
  RecordCardSkeleton,
  LoadMoreButton,
} from "@/components/generic";

// ============================================
// Types
// ============================================

export interface RevenuesListProps {
  /**
   * Array of revenue records to display.
   * Optional when inside an ExpensesProvider — reads from context.
   */
  records?: Expense[];

  /**
   * Loading state for initial load.
   * Optional when inside an ExpensesProvider — reads from context.
   */
  loading?: boolean;

  /** Loading state for loading more items. Optional — reads from context. */
  loadingMore?: boolean;

  /** Whether there are more items to load. Optional — reads from context. */
  hasMore?: boolean;

  /** Callback to load more items. Optional — reads from context. */
  onLoadMore?: () => void;

  /** Currently selected record IDs */
  selectedIds?: string[];

  /** Callback when selection changes */
  onSelectionChange?: (selectedIds: string[]) => void;

  /** Callback when edit action is clicked */
  onEdit?: (record: Expense) => void;

  /** Callback when delete action is clicked */
  onDelete?: (record: Expense) => void;

  /** Callback when a record card is clicked */
  onRecordClick?: (record: Expense) => void;

  /** ID of record currently being deleted. Optional — reads from context. */
  deletingId?: string | null;

  /** ID of recently edited/added record to highlight */
  highlightedId?: string | null;

  /** Callback when add button is clicked (for empty state) */
  onAdd?: () => void;

  /** Whether to enable selection */
  selectable?: boolean;
}

// ============================================
// Loading Skeleton
// ============================================

const LoadingSkeleton: React.FC = () => {
  return (
    <Stack gap="md">
      {Array.from({ length: 4 }).map((_, index) => (
        <RecordCardSkeleton
          key={index}
          showPrice={true}
          customContentRows={index % 2 === 0 ? 2 : 1}
        />
      ))}
    </Stack>
  );
};

// ============================================
// Revenue Card Content
// ============================================

interface RevenueContentProps {
  record: Expense;
  getRevenueKindName: (id: number | undefined) => string;
}

const RevenueContentComponent: React.FC<RevenueContentProps> = ({
  record,
  getRevenueKindName,
}) => {
  const kindName = getRevenueKindName(record.kindId);

  return (
    <>
      {/* Category badge */}
      {kindName && (
        <Badge variant="light" size="sm" color="teal">
          {kindName}
        </Badge>
      )}

      {/* Short note */}
      {record.shortNote && <Text size="md">{record.shortNote}</Text>}
    </>
  );
};

const RevenueContent = React.memo(RevenueContentComponent);

// ============================================
// Single Revenue Card — delegates to RecordCard
// ============================================

interface RevenueCardProps {
  record: Expense;
  selected: boolean;
  onSelect: (id: string, checked: boolean) => void;
  onClick?: (record: Expense) => void;
  onEdit?: (record: Expense) => void;
  onDelete?: (record: Expense) => void;
  selectable: boolean;
  highlighted: boolean;
  isDeleting: boolean;
  currency: string;
  odometerUnit: string;
  hasOdometerAnomaly: boolean;
  getRevenueKindName: (id: number | undefined) => string;
  // Swipe management props
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const RevenueCardComponent: React.FC<RevenueCardProps> = ({
  record,
  selected,
  onSelect,
  onClick,
  onEdit,
  onDelete,
  selectable,
  highlighted,
  isDeleting,
  currency,
  odometerUnit,
  hasOdometerAnomaly,
  getRevenueKindName,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const carLabel = record.car?.label;

  return (
    <RecordCard
      record={record}
      recordId={record.id}
      whenDone={record.whenDone}
      carLabel={carLabel}
      odometer={record.odometer}
      distanceLabel={odometerUnit}
      hasOdometerAnomaly={hasOdometerAnomaly}
      whereDone={record.whereDone}
      location={record.location}
      weatherData={record}
      tags={record.tags}
      totalPrice={record.totalPrice}
      priceCurrency={record.paidInCurrency}
      fallbackCurrency={currency}
      priceColor="teal.7"
      selected={selected}
      onSelect={onSelect}
      selectable={selectable}
      highlighted={highlighted}
      isDeleting={isDeleting}
      onClick={onClick}
      onEdit={onEdit}
      onDelete={onDelete}
      forceClose={forceClose}
      onForceCloseComplete={onForceCloseComplete}
      onSwipeStateChange={onSwipeStateChange}
    >
      <RevenueContent
        record={record}
        getRevenueKindName={getRevenueKindName}
      />
    </RecordCard>
  );
};

const RevenueCard = React.memo(RevenueCardComponent);

// ============================================
// Main Component
// ============================================

const RevenuesListComponent: React.FC<RevenuesListProps> = ({
  records: recordsProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  selectedIds = [],
  onSelectionChange,
  onEdit,
  onDelete,
  onRecordClick,
  deletingId: deletingIdProp,
  highlightedId,
  onAdd,
  selectable = true,
}) => {
  const { t } = useTranslation(["revenues", "common"]);
  const {
    currency,
    distanceLabelShort,
  } = useUserProfileContext();
  const { getRevenueKindNameById } = useAppEssentialsContext();
  const { getCarById } = useDashboardContext();

  // ── Context vs Props resolution ──
  const expensesContext = useOptionalExpensesContext();

  const records = recordsProp ?? expensesContext?.expenses ?? [];
  const loading = loadingProp ?? expensesContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? expensesContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? expensesContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? expensesContext?.loadMore;
  const deletingId = deletingIdProp ?? expensesContext?.removingId ?? null;

  // Manage swipeable cards
  const {
    openCard,
    closeAllCards,
    shouldForceClose,
    handleForceCloseComplete,
  } = useSwipeableList();

  // Entrance animation — fires once when data first arrives
  const { hasAnimated } = useEntranceAnimation({ itemCount: records.length });

  // ── Odometer anomalies ──

  const odometerAnomalies = useMemo(
    () => detectOdometerAnomalies(records),
    [records]
  );

  // ── Selection ──

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const handleSelect = useCallback(
    (id: string, checked: boolean) => {
      if (!onSelectionChange) return;
      const newSelection = checked
        ? [...selectedIds, id]
        : selectedIds.filter((sid) => sid !== id);
      onSelectionChange(newSelection);
    },
    [selectedIds, onSelectionChange]
  );

  // ── Card interactions ──

  const handleRecordClick = useCallback(
    (record: Expense) => {
      closeAllCards();
      onRecordClick?.(record);
    },
    [onRecordClick, closeAllCards]
  );

  const handleEdit = useCallback(
    (record: Expense) => {
      closeAllCards();
      onEdit?.(record);
    },
    [onEdit, closeAllCards]
  );

  const handleDelete = useCallback(
    (record: Expense) => {
      closeAllCards();
      onDelete?.(record);
    },
    [onDelete, closeAllCards]
  );

  // ── Swipe state ──

  const handleSwipeStateChange = useCallback(
    (recordId: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(recordId);
      }
    },
    [openCard]
  );

  // ── Helper: odometer unit per record ──

  const getOdometerUnit = useCallback(
    (record: Expense): string => {
      const car = getCarById(record.carId);
      return car?.mileageIn || distanceLabelShort;
    },
    [getCarById, distanceLabelShort]
  );

  // ── Loading state ──

  if (loading && records.length === 0) {
    return <LoadingSkeleton />;
  }

  // ── Empty state ──

  if (!loading && records.length === 0) {
    return (
      <Paper withBorder p="xl">
        <EmptyState
          title={t("revenues:emptyState.title")}
          description={t("revenues:emptyState.description")}
          iconName="CurrencyDollar"
          actionLabel={t("revenues:emptyState.actionLabel")}
          onAction={onAdd}
          size="md"
        />
      </Paper>
    );
  }

  return (
    <Stack gap="md">
      {/* Card list with animations */}
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--mantine-spacing-md)",
        }}
      >
        <AnimatePresence mode="sync">
          {records.map((record) => (
            <motion.div
              key={record.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              <RevenueCard
                record={record}
                selected={selectedSet.has(record.id || "")}
                onSelect={handleSelect}
                onClick={handleRecordClick}
                onEdit={onEdit ? handleEdit : undefined}
                onDelete={onDelete ? handleDelete : undefined}
                selectable={selectable}
                highlighted={record.id === highlightedId}
                isDeleting={deletingId === record.id}
                currency={currency}
                odometerUnit={t(`common:${getOdometerUnit(record)}`)}
                hasOdometerAnomaly={odometerAnomalies.has(record.id || "")}
                getRevenueKindName={getRevenueKindNameById}
                forceClose={shouldForceClose(record.id || "")}
                onForceCloseComplete={() =>
                  handleForceCloseComplete(record.id || "")
                }
                onSwipeStateChange={(isOpen) =>
                  handleSwipeStateChange(record.id || "", isOpen)
                }
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      <LoadMoreButton
        hasMore={hasMore}
        loading={loading}
        loadingMore={loadingMore}
        onLoadMore={onLoadMore}
      />
    </Stack>
  );
};

export const RevenuesList = React.memo(RevenuesListComponent);